import i18n from "../i18n";
import type { MetricsReportDayOfWeek } from "./metricsReportSchedule";
import { chileanYmdFromIso, formatYmd, parseYmdToLocalDate } from "./dates";

export type MetricsReportPeriod = "last_week" | "last_month";

export type MetricsReportPeriodRange = {
  startDate: string;
  endDate: string;
};

function periodOption(value: MetricsReportPeriod) {
  return {
    value,
    get label() {
      return i18n.t(`miContenido:reportPeriod.${value}.label`);
    },
    get summary() {
      return i18n.t(`miContenido:reportPeriod.${value}.summary`);
    },
    get explanation() {
      return i18n.t(`miContenido:reportPeriod.${value}.explanation`);
    },
  };
}

export const METRICS_REPORT_PERIOD_OPTIONS: {
  value: MetricsReportPeriod;
  label: string;
  /** Short line under the select */
  summary: string;
  /** Longer explanation for the preview block */
  explanation: string;
}[] = [periodOption("last_week"), periodOption("last_month")];

export function metricsReportPeriodOption(period: MetricsReportPeriod) {
  return (
    METRICS_REPORT_PERIOD_OPTIONS.find((o) => o.value === period) ??
    METRICS_REPORT_PERIOD_OPTIONS[0]
  );
}

/** ISO weekday from YYYY-MM-DD (1 = Monday … 7 = Sunday). */
export function isoWeekdayFromYmd(ymd: string): number {
  const js = parseYmdToLocalDate(ymd).getDay();
  return js === 0 ? 7 : js;
}

export function addDaysYmd(ymd: string, days: number): string {
  const d = parseYmdToLocalDate(ymd);
  d.setDate(d.getDate() + days);
  return formatYmd(d);
}

export function chileanTodayYmd(): string {
  return chileanYmdFromIso(new Date().toISOString());
}

export function lastSundayBeforeSendDate(sendDateYmd: string): string {
  const isoDay = isoWeekdayFromYmd(sendDateYmd);
  return addDaysYmd(sendDateYmd, -isoDay);
}

export function computeMetricsReportPeriodRange(
  reportPeriod: MetricsReportPeriod,
  sendDateYmd: string,
): MetricsReportPeriodRange {
  const endDate = lastSundayBeforeSendDate(sendDateYmd);

  if (reportPeriod === "last_week") {
    return {
      startDate: addDaysYmd(endDate, -6),
      endDate,
    };
  }

  return {
    startDate: addDaysYmd(endDate, -27),
    endDate,
  };
}

export function nextSendDateYmd(
  dayOfWeek: MetricsReportDayOfWeek,
  fromYmd: string = chileanTodayYmd(),
): string {
  const fromIso = isoWeekdayFromYmd(fromYmd);
  let delta = dayOfWeek - fromIso;
  if (delta < 0) delta += 7;
  return addDaysYmd(fromYmd, delta);
}

export function formatMetricsReportPeriodPreview(
  reportPeriod: MetricsReportPeriod,
  dayOfWeek: MetricsReportDayOfWeek,
): { sendDate: string; range: MetricsReportPeriodRange } {
  const sendDate = nextSendDateYmd(dayOfWeek);
  const range = computeMetricsReportPeriodRange(reportPeriod, sendDate);
  return { sendDate, range };
}
